import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaTimes, FaPlus, FaEdit, FaTrash, FaSave, FaBan, FaFilter, FaFilterCircleXmark,
  FaTimes as FaCancel, FaSort, FaSortUp, FaSortDown, FaExpand, 
  FaCompress, FaLock, FaArrowsAltV, FaClone, FaDatabase,
  FaSpinner, FaCopy, FaCogs, FaEye, FaExternalLinkAlt, FaSortAmountDown // AGGIUNTO: Per il link alle tabelle e ordinamento
} from 'react-icons/fa';
import { useToast } from '../../hooks/useToast'; // CORREZIONE: Usa il hook, non il context
import { logger } from '../../utils/logger';
import { apiService } from '../../services/apiService'; // CORREZIONE: Solo apiService è esportato
import api from '../../config/axios'; // AGGIUNTO: usa la stessa axios della TabellePage
import DuplicaCampiModal from './DuplicaCampiModal';
import OrdinamentoCampiModal from './OrdinamentoCampiModal'; // AGGIUNTO: Modal per configurare ordinamento campi
import CampiOperatoriEditor from './CampiOperatoriEditor'; // NUOVO: Sub-editor SKE_CampiOperatori
import CensimentoDifferencesModal from '../common/CensimentoDifferencesModal';
import FormInput from '../data/TableManager/forms/FormInput';
import DragDropSequenceManager from '../../common/DragDropSequenceManager'; // CORREZIONE: Path corretto
import useLookupOptions from '../../hooks/useLookupOptions'; // DEFAULT EXPORT
import { hubFields } from '../../constants/miloFields';

const CampiTabellaModal = ({
  isOpen,
  onClose,
  currentTableId,
  initialCampi = [],
  isLoadingCampi,
  onCampiUpdated,
  themeColor = 'emerald',
  macrofunzioneData, // AGGIUNTO: Per mostrare nome e ID macrofunzione nell'header
}) => {
  // AGGIUNTO: Hook per navigazione
  const navigate = useNavigate();
  
  // AGGIUNTO: Hook per le notifiche
  const { showSuccess, showError } = useToast();
  
  // AGGIUNTO: Funzione per navigare alla pagina delle tabelle con filtro
  const handleNavigateToTabelle = useCallback(() => {
    if (!currentTableId) {
      showError("Nessuna tabella selezionata per la navigazione.");
      return;
    }

    // Naviga alla pagina delle tabelle passando il filtro per TAB_Id_Tabelle
    navigate('/sisketron/tabelle', { // CORREZIONE: Era '/tabelle', ora '/sisketron/tabelle'
      state: {
        presetFilter: {
          TAB_Id_Tabelle: currentTableId
        }
      }
    });
  }, [currentTableId, navigate, showError]);

  const [displayCampi, setDisplayCampi] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingCampoId, setEditingCampoId] = useState(null);
  const [editingCampoData, setEditingCampoData] = useState({});
  const [newCampoData, setNewCampoData] = useState({
    CAM_Nome: '',
    CAM_Descrizione: '',
    CAM_Id_TipiCampi: '',
    CAM_Sequenza: '',
    CAM_Lunghezza: '', // Nessun valore di default
    CAM_Nullo: false,
    CAM_Incrementale: false,
    CAM_Like: false,
    CAM_AccettaZero: false,
    CAM_Scollegato: false,
    CAM_ListaInFiltro: 2, // ✅ CAMBIATO: Da 1 a 2 (da "Lista" a "Singolo")
    CAM_Default: '',
    CAM_NomeCampoChiaveEsterno: '',
    CAM_Suffisso: '',
    // NUOVO: campi MILO/HUB (usati solo se la tabella appartiene al modulo HUB)
    CAM_ChiaveNaturale: false,
    CAM_Cercabile: false,
    CAM_Versioned: true,
    CAM_Cifrato: false,
    CAM_NomeUi: '',
    CAM_ErroreValidazione: '',
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [sortField, setSortField] = useState('CAM_Sequenza');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [isEditFormExpanded, setIsEditFormExpanded] = useState(false);
  const [isSequenceManagerOpen, setIsSequenceManagerOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isOrdinamentoModalOpen, setIsOrdinamentoModalOpen] = useState(false); // AGGIUNTO: Modal ordinamento
  const [isCensimentoInCorso, setIsCensimentoInCorso] = useState(false);

  // NUOVO: state per il sub-editor SKE_CampiOperatori
  const [operatoriEditorState, setOperatoriEditorState] = useState({
    isOpen: false,
    campoId: null,
    campoNome: '',
    cercabile: true,
  });

  // AGGIUNTO: Stato per modale differenze censimento
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [pendingDifferences, setPendingDifferences] = useState([]);
  const [pendingTableName, setPendingTableName] = useState('');
  const [pendingCensimentoPayload, setPendingCensimentoPayload] = useState(null);

  // NUOVO: Ref per tracciare se stiamo eseguendo operazioni
  const isPerformingOperation = useRef(false);
  const lastUpdateTime = useRef(0);
  const pendingRefresh = useRef(false);
  const addFormRef = useRef(null);
  const editRowRef = useRef(null);

  // Configuration for Lookups
  const lookupConfig = useMemo(() => ({
    tipiCampiLookup: {
      tableName: 'SKE_TipiCampi',
      valueField: 'TIP_Id_TipiCampi',
      labelField: 'TIP_Descrizione',
    },
    tabelleLookup: {
      tableName: 'SKE_Tabelle',
      valueField: 'TAB_Id_Tabelle',
      labelField: 'TAB_Nome',
      // ✅ CORREZIONE: Aggiungi TAB_GeneraFiltro nei campi aggiuntivi
      additionalFields: ['TAB_Id_Moduli', 'TAB_Descrizione', 'TAB_Prefisso', 'TAB_GeneraFiltro'],
    },
  }), []);

  const { 
    lookupOptions, 
    loading: isLoadingLookups, 
    error: lookupError 
  } = useLookupOptions(lookupConfig);

  // Definizione dei campi principali e secondari per la gestione della vista
  const mainFields = ['CAM_Nome', 'CAM_Descrizione', 'CAM_Id_TipiCampi', 'CAM_Lunghezza'];

  // NOTA: per le tabelle del modulo HUB/MILO viene
  // utilizzata la modale dedicata `CampiTableModalHub` (instradata dal
  // componente padre). Qui forziamo `isHubTable = false` in modo che i 6
  // nuovi campi `CAM_*` MILO e il sub-editor SKE_CampiOperatori non vengano
  // MAI mostrati in questa modale "standard". Vincolo: zero modifiche
  // comportamentali per i moduli non-HUB.
  const isHubTable = false;

  const extendedFields = useMemo(() => {
    const base = [
      'CAM_Sequenza', 'CAM_Nullo', 'CAM_Incrementale',
      'CAM_Like','CAM_AccettaZero','CAM_Scollegato', 'CAM_ListaInFiltro', 'CAM_Default', 'CAM_Extra_Tabelle',
      'CAM_NomeCampoChiaveEsterno', 'CAM_Suffisso'
    ];
    return isHubTable ? [...base, ...hubFields] : base;
  }, [isHubTable]);

  // CORREZIONE: useEffect che aggiorna sempre quando cambiano initialCampi
  useEffect(() => {
    // Se non è aperta la modal, non fare niente
    if (!isOpen) return;

    // Se non ci sono campi iniziali, inizializza array vuoto
    if (!initialCampi || initialCampi.length === 0) {
      console.log('🧹 CampiTabellaModal: Inizializzando array vuoto');
      setDisplayCampi([]);
      return;
    }

    // RIMOSSO il controllo isPerformingOperation che impediva l'aggiornamento
    console.log('🔄 CampiTabellaModal: Aggiornando displayCampi da initialCampi', initialCampi.length, 'campi');
    const sortedCampi = [...initialCampi].sort((a, b) => (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0));
    setDisplayCampi(sortedCampi);
    lastUpdateTime.current = Date.now();
  }, [isOpen, initialCampi]);

  // MODIFICATO: Gestore refresh più semplice e diretto
  const handleSmartRefresh = useCallback(() => {
    console.log('🔄 CampiTabellaModal: Eseguendo refresh immediato');
    
    // Reset immediato del flag operazione
    isPerformingOperation.current = false;
    pendingRefresh.current = false;
    
    // Chiama direttamente onCampiUpdated per ricaricare i dati freschi dal server
    if (onCampiUpdated) {
      onCampiUpdated();
    }
  }, [onCampiUpdated]);

  // NUOVO: Esegui refresh immediato dopo operazioni critiche
  const executeImmediateRefresh = useCallback(() => {
    console.log('⚡ CampiTabellaModal: Refresh immediato forzato');
    
    // Reset completo dello stato
    isPerformingOperation.current = false;
    pendingRefresh.current = false;
    
    // Chiama direttamente onCampiUpdated senza delay
    if (onCampiUpdated) {
      console.log('🔄 CampiTabellaModal: Chiamando onCampiUpdated');
      onCampiUpdated();
    }
  }, [onCampiUpdated]);

  // AGGIUNTO: Funzione per censimento automatico item (pattern identico a TabelleCustomActions.jsx)
  const [generationResult, setGenerationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  // CORREZIONE COMPLETA: Funzione per censimento automatico item con fallback per TAB_GeneraFiltro
  const handleCensimentoAutomaticoItem = useCallback(async () => {
    if (!currentTableId) {
      showError('Nessuna tabella selezionata.');
      return;
    }

    setIsCensimentoInCorso(true);
    
    try {
      // ✅ SOLUZIONE: Usa i dati dei lookup già caricati con fallback per TAB_GeneraFiltro
      console.log('[CampiTabellaModal] Cercando tabella nei lookup:', currentTableId);
      console.log('[CampiTabellaModal] Lookup disponibili:', lookupOptions?.tabelleLookup?.length || 0);
      
      const tabelleLookup = lookupOptions?.tabelleLookup || [];
      const tabellaRecord = tabelleLookup.find(t => 
        t.value === currentTableId || 
        t.original?.TAB_Id_Tabelle === currentTableId
      );
      
      console.log('[CampiTabellaModal] Record tabella trovato:', tabellaRecord);
      
      let tabellaCompleta = null;
      
      if (!tabellaRecord) {
        // Fallback: prova a fare una query list invece di single record
        console.log('[CampiTabellaModal] Tabella non trovata nei lookup, provo con query list');
        
        try {
          const listResponse = await api.get('/crud/SKE_Tabelle', {
            params: {
              filters: JSON.stringify({ TAB_Id_Tabelle: currentTableId }),
              limit: 1
            }
          });
          
          if (!listResponse.data?.records || listResponse.data.records.length === 0) {
            throw new Error(`Tabella con ID ${currentTableId} non trovata`);
          }
          
          tabellaCompleta = listResponse.data.records[0];
          console.log('[CampiTabellaModal] Dati tabella da query list:', tabellaCompleta);
        } catch (apiError) {
          console.warn('[CampiTabellaModal] Errore query list:', apiError);
          throw new Error('Impossibile recuperare i dati della tabella');
        }
      } else {
        // Usa i dati dal record originale del lookup
        tabellaCompleta = tabellaRecord.original || tabellaRecord;
        console.log('[CampiTabellaModal] Dati tabella da lookup:', tabellaCompleta);
      }
      
      // ✅ DEBUGGING AVANZATO: Verifica esplicita del campo TAB_GeneraFiltro
      console.log('[CampiTabellaModal] TAB_GeneraFiltro raw value:', tabellaCompleta.TAB_GeneraFiltro);
      console.log('[CampiTabellaModal] TAB_GeneraFiltro type:', typeof tabellaCompleta.TAB_GeneraFiltro);
      console.log('[CampiTabellaModal] Tutti i campi della tabella:', Object.keys(tabellaCompleta));
      
      let generaFiltro = false;
      
      // ✅ FALLBACK: Se TAB_GeneraFiltro è undefined nei lookup, prova una query specifica
      if (tabellaCompleta.TAB_GeneraFiltro === undefined) {
        console.log('[CampiTabellaModal] TAB_GeneraFiltro non presente nei lookup, tentativo query specifica');
        
        try {
          // Query specifica per ottenere solo il campo TAB_GeneraFiltro
          const filtroResponse = await api.get('/crud/SKE_Tabelle', {
            params: {
              filters: JSON.stringify({ TAB_Id_Tabelle: currentTableId }),
              fields: 'TAB_Id_Tabelle,TAB_GeneraFiltro',
              limit: 1
            }
          });
          
          if (filtroResponse.data?.records && filtroResponse.data.records.length > 0) {
            const filtroData = filtroResponse.data.records[0];
            console.log('[CampiTabellaModal] Dati TAB_GeneraFiltro da query specifica:', filtroData);
            generaFiltro = !!filtroData.TAB_GeneraFiltro;
            
            // Aggiorna anche tabellaCompleta con il valore corretto
            tabellaCompleta.TAB_GeneraFiltro = filtroData.TAB_GeneraFiltro;
          } else {
            console.warn('[CampiTabellaModal] Query specifica per TAB_GeneraFiltro fallita, usando default false');
            generaFiltro = false;
          }
        } catch (fallbackError) {
          console.warn('[CampiTabellaModal] Errore query fallback TAB_GeneraFiltro:', fallbackError);
          console.log('[CampiTabellaModal] Usando valore di default false per filtro');
          generaFiltro = false;
        }
      } else {
        generaFiltro = !!tabellaCompleta.TAB_GeneraFiltro;
      }
      
      console.log('[CampiTabellaModal] ✅ VALORE FINALE TAB_GeneraFiltro:', {
        raw: tabellaCompleta.TAB_GeneraFiltro,
        boolean: generaFiltro,
        source: tabellaCompleta.TAB_GeneraFiltro === undefined ? 'fallback_query' : 'lookup_data'
      });

      console.log('[CampiTabellaModal] Dati tabella recuperati:', {
        tableId: currentTableId,
        TAB_GeneraFiltro: tabellaCompleta.TAB_GeneraFiltro,
        generaFiltro,
        source: tabellaRecord ? 'lookup' : 'list_query',
        allFields: Object.keys(tabellaCompleta),
        tabellaCompleta
      });

      // ✅ CORREZIONE: Payload identico a TabelleCustomActions.jsx
      const payload = {
        idTabella: currentTableId,
        filtro: generaFiltro,
      };

      console.log('[CampiTabellaModal] Payload finale per censimento:', payload);

      // STEP 1: Dry run — esegui censimento in transazione, rileva differenze, rollback
      const dryRunPayload = { ...payload, dryRun: true };
      logger.info('[CampiTabellaModal] DryRun payload:', dryRunPayload);
      const dryRunResponse = await api.post('/censimento/oggetto-da-tabella', dryRunPayload);
      logger.info('[CampiTabellaModal] DryRun risposta:', dryRunResponse.data);

      if (dryRunResponse.data?.success && dryRunResponse.data.dryRun && dryRunResponse.data.hasDifferences) {
        // Ci sono differenze: mostra modale
        setPendingDifferences(dryRunResponse.data.differences);
        setPendingTableName(dryRunResponse.data.tableName || '');
        setPendingCensimentoPayload(payload);
        setShowDiffModal(true);
        setIsCensimentoInCorso(false);
        return;
      }

      // Nessuna differenza (o primo censimento): procedi direttamente
      await executeCensimentoEffettivo(payload);
      
    } catch (error) {
      console.error('[CampiTabellaModal] Errore durante il censimento:', error);
      
      const result = {
        success: false,
        message: error.message || 'Errore durante il censimento',
        error: error,
        timestamp: new Date().toISOString()
      };
      
      setGenerationResult(result);
      setShowResultModal(true);
      showError(`Errore: ${error.message || 'Errore durante il censimento'}`);
    } finally {
      setIsCensimentoInCorso(false);
    }
  }, [currentTableId, lookupOptions, showError, showSuccess, executeImmediateRefresh]);

  // Esegue il censimento effettivo (con o senza preserveMap)
  const executeCensimentoEffettivo = useCallback(async (basePayload, preserveMap = null) => {
    setIsCensimentoInCorso(true);
    setShowDiffModal(false);
    try {
      const payload = {
        ...basePayload,
        dryRun: false,
        preserveMap: preserveMap,
      };
      logger.info('[CampiTabellaModal] Payload censimento definitivo:', payload);
      const response = await api.post('/censimento/oggetto-da-tabella', payload);
      logger.info('[CampiTabellaModal] Risposta censimento:', response.data);

      if (response.data?.success) {
        const result = {
          success: true,
          message: 'Censimento completato con successo',
          data: response.data,
          generaFiltro: basePayload.filtro,
          timestamp: new Date().toISOString()
        };
        setGenerationResult(result);
        setShowResultModal(true);
        showSuccess(`Censimento completato! ${basePayload.filtro ? 'Generati Item principale e filtro.' : 'Generato solo Item principale.'}`);
        executeImmediateRefresh();
      } else {
        throw new Error(response.data?.message || 'Risposta del server non valida');
      }
    } catch (error) {
      console.error('[CampiTabellaModal] Errore durante il censimento effettivo:', error);
      const result = {
        success: false,
        message: error.message || 'Errore durante il censimento',
        error: error,
        timestamp: new Date().toISOString()
      };
      setGenerationResult(result);
      setShowResultModal(true);
      showError(`Errore: ${error.message || 'Errore durante il censimento'}`);
    } finally {
      setIsCensimentoInCorso(false);
    }
  }, [showError, showSuccess, executeImmediateRefresh]);

  // Callback modale differenze: conferma con preservazione
  const handleDiffConfirm = useCallback((preserveMap) => {
    if (pendingCensimentoPayload) {
      executeCensimentoEffettivo(pendingCensimentoPayload, preserveMap);
    }
  }, [pendingCensimentoPayload, executeCensimentoEffettivo]);

  // Callback modale differenze: annulla
  const handleDiffCancel = useCallback(() => {
    setShowDiffModal(false);
    setPendingDifferences([]);
    setPendingTableName('');
    setPendingCensimentoPayload(null);
    setIsCensimentoInCorso(false);
  }, []);

  // AGGIUNTO: Funzione per mostrare i risultati
  const handleShowResults = () => {
    if (generationResult) {
      setShowResultModal(true);
    }
  };

  // AGGIUNTO: Funzione per rendere i dettagli dei risultati (identica a TabelleCustomActions)
  const renderResultDetails = () => {
    if (!generationResult || generationResult.type !== 'table') return null;
    const { data } = generationResult;
    if (!data) return <p>Nessun dato dettagliato disponibile.</p>;

    return (
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Oggetti e Attributi Generati</h4>
        {data.mainObject && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Oggetto Principale {data.mainObject.operation && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded ml-2">
                  {data.mainObject.operation === 'create' ? 'Creato' : 'Aggiornato'}
                </span>
              )}
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><strong>ID:</strong> {data.mainObject.objectId}</div>
              <div><strong>Nome:</strong> {data.mainObject.objectName}</div>
              <div><strong>Attributi Creati:</strong> {data.mainObject.attributesCount}</div>
            </div>
            {data.mainObject.createdAttributes && data.mainObject.createdAttributes.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Dettaglio Attributi ({data.mainObject.createdAttributes.length})</summary>
                <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                  {data.mainObject.createdAttributes.map((attr, idx) => (
                    <li key={idx}>
                      {attr.name} (ID: {attr.id}, Tipo: {attr.fieldType || attr.type}, Sorgente: {attr.sourceField || 'N/A'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {data.filterObject && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Oggetto Filtro {data.filterObject.operation && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded ml-2">
                {data.filterObject.operation === 'create' ? 'Creato' : 'Aggiornato'}
              </span>
              )}
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><strong>ID:</strong> {data.filterObject.objectId}</div>
              <div><strong>Nome:</strong> {data.filterObject.objectName}</div>
              <div><strong>Attributi Creati:</strong> {data.filterObject.attributesCount}</div>
            </div>
            {data.filterObject.createdAttributes && data.filterObject.createdAttributes.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Dettaglio Attributi ({data.filterObject.createdAttributes.length})</summary>
                <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                  {data.filterObject.createdAttributes.map((attr, idx) => (
                    <li key={idx}>
                      {attr.name} (ID: {attr.id}, Tipo: {attr.fieldType || attr.type}, Sorgente: {attr.sourceField || 'N/A'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  const isReadOnlyCampo = (campo) => {
    const sequenza = Number(campo.CAM_Sequenza) || 0;
    return sequenza === 0 || sequenza > 1000;
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    
    const sortedCampi = [...displayCampi].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return newDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setDisplayCampi(sortedCampi);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="ml-1 text-gray-400" />;
    return sortDirection === 'asc' ? <FaSortUp className="ml-1" /> : <FaSortDown className="ml-1" />;
  };

  // Tipi campo numerici (Intero, Decimale, Long, Decimale BIGDECIMAL)
  const TIPI_NUMERICI = [2, 5, 13, 15];

  const validateCampoData = (data) => {
    const errors = {};
    if (!data.CAM_Nome?.trim()) errors.CAM_Nome = 'Il nome è obbligatorio';
    if (!data.CAM_Descrizione?.trim()) errors.CAM_Descrizione = 'La descrizione è obbligatoria';
    if (!data.CAM_Id_TipiCampi) errors.CAM_Id_TipiCampi = 'Il tipo è obbligatorio';
    
    const isNumerico = TIPI_NUMERICI.includes(Number(data.CAM_Id_TipiCampi));
    if (isNumerico && (!data.CAM_Lunghezza || Number(data.CAM_Lunghezza) <= 0)) {
      errors.CAM_Lunghezza = 'La lunghezza è obbligatoria per i campi numerici';
    } else if (data.CAM_Lunghezza && Number(data.CAM_Lunghezza) <= 0) {
      errors.CAM_Lunghezza = 'Se specificata, la lunghezza deve essere maggiore di 0';
    }
    return errors;
  };

  const handleSaveEdit = async () => {
    if (!editingCampoData) return;

    const validationErrors = validateCampoData(editingCampoData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError('Completa tutti i campi obbligatori');
      return;
    }

    const isNewDuplicate = editingCampoData._isNewDuplicate || editingCampoData.CAM_Id_Campi < 0;
    
    console.log('💾 CampiTabellaModal: Salvando', isNewDuplicate ? 'nuovo campo duplicato' : 'modifica campo', editingCampoData.CAM_Id_Campi);
    
    isPerformingOperation.current = true;
    setIsSaving(true);
    
    try {
      let response;
      
      // ✅ AGGIUNTO: Normalizza i dati prima dell'invio
      const dataToSend = { 
        ...editingCampoData,
        CAM_Extra_Tabelle: editingCampoData.CAM_Extra_Tabelle || 0
      };

      if (isNewDuplicate) {
        // NUOVO CAMPO DUPLICATO: Crea sul server
        const originalSequenza = Number(dataToSend.CAM_Sequenza) || 0;
        
        // Prima, sposta tutti i campi successivi di +1 (solo quelli reali, non temporanei)
        const campiDaSpostare = displayCampi.filter(c => {
          const seq = Number(c.CAM_Sequenza) || 0;
          const isReal = c.CAM_Id_Campi > 0; // Solo campi reali (non temporanei)
          return isReal && seq > originalSequenza && seq <= 999;
        });

        if (campiDaSpostare.length > 0) {
          console.log(`🔄 Spostando ${campiDaSpostare.length} campi reali in avanti di 1 posizione`);
          
          const updatePayload = {
            changes: campiDaSpostare.map(campo => ({
              operation: 'update',
              data: {
                CAM_Id_Campi: campo.CAM_Id_Campi,
                CAM_Sequenza: (Number(campo.CAM_Sequenza) || 0) + 1
              }
            }))
          };

          await apiService.post('/crud/SKE_Campi', updatePayload);
          console.log('✅ Sequenze aggiornate con successo');
        }
        
        // Ora crea il nuovo campo
        const createData = { ...dataToSend };
        delete createData.CAM_Id_Campi; // Rimuovi l'ID temporaneo
        delete createData._isNewDuplicate; // Rimuovi il flag
        
        response = await apiService.post('/crud/SKE_Campi', {
          changes: [{ 
            operation: 'create', 
            data: createData 
          }]
        });
        
        if (response && response.results && response.results.success) {
          showSuccess('Campo duplicato e salvato con successo');
        }
      } else {
        // MODIFICA CAMPO ESISTENTE: Logica normale
        response = await apiService.post('/crud/SKE_Campi', {
          changes: [{ 
            operation: 'update', 
            data: dataToSend // Usa i dati normalizzati
          }]
        });
        
        if (response && response.results && response.results.success) {
          showSuccess('Campo modificato con successo');
        }
      }

      if (response && response.results && response.results.success) {
        setEditingCampoId(null);
        setEditingCampoData({});
        setErrors({});
        
        // Refresh immediato per avere i dati aggiornati dal server
        executeImmediateRefresh();
      } else {
        console.log('❌ CampiTabellaModal: Errore nel salvataggio');
        showError('Errore durante il salvataggio del campo');
        isPerformingOperation.current = false;
      }
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      showError(`Errore durante il salvataggio: ${error.message}`);
      isPerformingOperation.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNewCampo = async () => {
    const validationErrors = validateCampoData(newCampoData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError('Completa tutti i campi obbligatori');
      return;
    }

    console.log('➕ CampiTabellaModal: Aggiungendo nuovo campo');
    isPerformingOperation.current = true;
    setIsSaving(true);
    
    try {
      const campoToAdd = {
        ...newCampoData,
        CAM_Id_Tabelle: currentTableId,
        CAM_Sequenza: parseInt(newCampoData.CAM_Sequenza) || 1,
        CAM_Extra_Tabelle: newCampoData.CAM_Extra_Tabelle || 0 // ✅ AGGIUNTO: Normalizza il valore
      };

      // RIMOSSO: Aggiornamento ottimistico

      const response = await apiService.post('/crud/SKE_Campi', {
        changes: [{ 
          operation: 'create', 
          data: campoToAdd 
        }]
      });

      if (response && response.results && response.results.success) {
        setNewCampoData({
          CAM_Nome: '',
          CAM_Descrizione: '',
          CAM_Id_TipiCampi: '',
          CAM_Sequenza: '',
          CAM_Lunghezza: '', // Nessun valore di default
          CAM_Nullo: false,
          CAM_Incrementale: false,
          CAM_Like: false,
          CAM_AccettaZero: false,
          CAM_Scollegato: false,
          CAM_ListaInFiltro: 2, // ✅ CAMBIATO: Da 1 a 2 (da "Lista" a "Singolo")
          CAM_Default: '',
          CAM_Extra_Tabelle: '',
          CAM_NomeCampoChiaveEsterno: '',
          CAM_Suffisso: '',
        });
        setIsAdding(false);
        setErrors({});
        showSuccess('Campo aggiunto con successo');
        
        // MODIFICATO: Refresh immediato
        executeImmediateRefresh();
      } else {
        console.log('❌ CampiTabellaModal: Errore aggiunta');
        showError('Errore durante l\'aggiunta del campo');
        isPerformingOperation.current = false;
      }
    } catch (error) {
      console.error('Errore durante l\'aggiunta:', error);
      showError(`Errore durante l'aggiunta: ${error.message}`);
      isPerformingOperation.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicateCampo = async (campoToDuplicate) => {
    // NUOVA LOGICA: Creare il campo duplicato localmente e metterlo in modalità edit
    // La creazione sul server avverrà solo quando l'utente salverà il form di modifica
    
    const originalSequenza = Number(campoToDuplicate.CAM_Sequenza) || 0;
    
    // Genera un ID temporaneo per il nuovo campo (negativo per distinguerlo)
    const tempId = -(Date.now()); // ID temporaneo negativo
    
    // Genera il nuovo nome del campo
    const newFieldName = `${campoToDuplicate.CAM_Nome}_copia`;
    
    // CORREZIONE: Genera automaticamente la descrizione dal nuovo nome
    const autoDescription = formatNameToDescription(newFieldName);
    
    // Prepara il campo duplicato con sequenza subito dopo l'originale
    const duplicatedData = {
      ...campoToDuplicate,
      CAM_Id_Campi: tempId, // ID temporaneo
      CAM_Nome: newFieldName,
      CAM_Descrizione: autoDescription, // CORREZIONE: Usa la descrizione auto-generata
      CAM_Sequenza: originalSequenza + 1,
      CAM_Incrementale: false,
      CAM_Id_Tabelle: currentTableId,
      _isNewDuplicate: true, // Flag per identificare che è un duplicato non ancora salvato
    };
    delete duplicatedData.CAM_Id_Campi_text; // Rimuovi eventuali campi di lookup
    delete duplicatedData.CAM_Id_TipiCampi_text;
    delete duplicatedData.CAM_Extra_Tabelle_text;

    console.log('📋 CampiTabellaModal: Creando duplicato locale con ID temporaneo:', tempId);
    console.log('🔄 Auto-descrizione generata:', `"${newFieldName}" -> "${autoDescription}"`);

    // Aggiorna la vista locale - sposta le sequenze e aggiungi il nuovo campo
    setDisplayCampi(prevCampi => {
      // Aggiorna le sequenze dei campi esistenti che vengono dopo l'originale
      const updatedCampi = prevCampi.map(campo => {
        const seq = Number(campo.CAM_Sequenza) || 0;
        if (seq > originalSequenza && seq <= 999) {
          return {
            ...campo,
            CAM_Sequenza: seq + 1
          };
        }
        return campo;
      });

      // Aggiungi il nuovo campo duplicato
      const finalCampi = [...updatedCampi, duplicatedData];
      return finalCampi.sort((a, b) => (Number(a.CAM_Sequenza) || 0) - (Number(b.CAM_Sequenza) || 0));
    });

    // ENTRA SUBITO IN MODALITÀ EDIT per il nuovo campo
    setEditingCampoId(tempId);
    setEditingCampoData({ ...duplicatedData });
    setIsAdding(false); // Assicurati che non sia in modalità "adding"

    showSuccess(`Campo duplicato localmente con descrizione auto-generata. Modifica i dati e salva per confermare.`);
  };

  const handleDuplicateSubmit = async (campiDaCreare) => {
    if (!campiDaCreare || campiDaCreare.length === 0) return;
    
    isPerformingOperation.current = true;
    setIsSaving(true);
    
    try {
      // NUOVO: Controlla se ci sono campi tecnici da duplicare
      const campiTecniciDaDuplicare = campiDaCreare.filter(campo => {
        const sequenza = Number(campo.CAM_Sequenza) || 0;
        return sequenza === 0 || sequenza > 1000;
      });
      
      // NUOVO: Se ci sono campi tecnici, verifica che la tabella sia vuota
      if (campiTecniciDaDuplicare.length > 0) {
        if (displayCampi.length > 0) {
          showError('I campi tecnici possono essere duplicati solo in tabelle vuote (senza campi esistenti)');
          isPerformingOperation.current = false;
          return;
        }
      }
      
      // Filtra i campi modificabili per calcolare la sequenza massima solo per campi applicativi
      const campiModificabili = displayCampi.filter(c => {
        const seq = Number(c.CAM_Sequenza) || 0;
        return seq > 0 && seq < 1000;
      });
      const maxSequenza = Math.max(0, ...campiModificabili.map(c => Number(c.CAM_Sequenza) || 0));
      
      // MODIFICATO: Gestione diversa della sequenza per campi tecnici vs applicativi
      const changes = campiDaCreare.map((campo, index) => {
        const sequenzaOriginale = Number(campo.CAM_Sequenza) || 0;
        const isCampoTecnico = sequenzaOriginale === 0 || sequenzaOriginale > 1000;
        
        let nuovaSequenza;
        if (isCampoTecnico) {
          // NUOVO: Per i campi tecnici, mantieni la sequenza originale
          nuovaSequenza = sequenzaOriginale;
        } else {
          // Per i campi applicativi, usa la logica esistente
          nuovaSequenza = maxSequenza + index + 1;
        }
        
        return {
          operation: 'create',
          data: {
            ...campo,
            CAM_Id_Tabelle: currentTableId,
            CAM_Sequenza: nuovaSequenza,
          }
        };
      });

      console.log('🔄 CampiTabellaModal: Inviando duplicazione', changes.length, 'campi');

      const response = await apiService.post('/crud/SKE_Campi', { changes });
      
      if (response && response.results && response.results.success) {
        const operationResults = response.results.results || [];
        const successCount = operationResults.filter(r => r.success).length;
        
        if (successCount > 0) {
          showSuccess(`${successCount} campi duplicati con successo!`);
          
          // NUOVO: Log dettagliato per campi tecnici
          if (campiTecniciDaDuplicare.length > 0) {
            logger.info(`[CampiTabellaModal] Duplicati ${campiTecniciDaDuplicare.length} campi tecnici con sequenze preservate`);
          }
          
          // MODIFICATO: Refresh immediato per mostrare i nuovi campi
          executeImmediateRefresh();
        } else {
          showError('Duplicazione fallita: nessun campo creato');
          isPerformingOperation.current = false;
        }
      } else {
        showError('Errore nella risposta del server');
        isPerformingOperation.current = false;
      }
    } catch (error) {
      logger.error("Errore durante la duplicazione dei campi:", error);
      showError(`Duplicazione fallita: ${error.message}`);
      isPerformingOperation.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCampo = async (campoId) => {
    console.log('🗑️ CampiTabellaModal: Eliminando campo', campoId);
    isPerformingOperation.current = true;
    
    try {
      // RIMOSSO: Aggiornamento ottimistico
      // updateDisplayCampiOptimistically(prev => prev.filter(campo => campo.CAM_Id_Campi !== campoId));
      
      const response = await apiService.post('/crud/SKE_Campi', {
        changes: [{ 
          operation: 'delete', 
          data: { CAM_Id_Campi: campoId } 
        }]
      });
      
      if (response && response.results && response.results.success) {
        showSuccess('Campo eliminato con successo');
        
        // MODIFICATO: Refresh immediato
        executeImmediateRefresh();
      } else {
        console.log('❌ CampiTabellaModal: Errore eliminazione');
        showError('Errore durante l\'eliminazione del campo');
        isPerformingOperation.current = false;
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione:', error);
      showError(`Errore durante l'eliminazione: ${error.message}`);
      isPerformingOperation.current = false;
    }
  };
  
  const handleEditCampo = (campo) => {
    setEditingCampoId(campo.CAM_Id_Campi);
    setEditingCampoData({ ...campo });
    setIsAdding(false);
    setIsEditFormExpanded(true);
    // Auto-scroll alla riga in editing dopo il render
    setTimeout(() => {
      editRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSequenceSave = async (updatedItems) => {
    logger.info(`[CampiTabellaModal] Ricevuti ${updatedItems.length} elementi per aggiornamento sequenza.`);
    
    const bulkOperations = updatedItems.map(item => ({
      operation: 'update',
      data: {
        CAM_Id_Campi: item.CAM_Id_Campi,
        CAM_Sequenza: item.CAM_Sequenza,
      },
    }));

    if (bulkOperations.length === 0) {
      logger.info("[CampiTabellaModal] Nessun elemento da aggiornare.");
      return true;
    }

    isPerformingOperation.current = true;
    setIsSaving(true);
    try {
      const response = await apiService.post('/crud/SKE_Campi', { changes: bulkOperations });
      
      if (response && response.results && response.results.success) {
        logger.info(`[CampiTabellaModal] Aggiornamento sequenza completato.`, response);
        
        showSuccess('Ordinamento salvato con successo!');
        
        // MODIFICATO: Refresh immediato
        executeImmediateRefresh();
        return true;
      } else {
        logger.error(`[CampiTabellaModal] Aggiornamento sequenza fallito.`, response);
        const errorMessage = response?.message || 'Errore durante l\'aggiornamento della sequenza';
        showError(errorMessage);
        isPerformingOperation.current = false;
        return false;
      }
    } catch (error) {
      logger.error(`[CampiTabellaModal] Eccezione durante aggiornamento sequenza.`, error);
      showError(`Errore salvataggio ordinamento: ${error.message}`);
      isPerformingOperation.current = false;
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Aggiungi questa funzione helper dopo le altre funzioni utility
const formatNameToDescription = (campoNome) => {
  if (!campoNome || typeof campoNome !== 'string') return '';
  
  // Rimuovi prefissi comuni (es. CAM_, MAC_, etc.)
  let cleanName = campoNome.replace(/^[A-Z]{2,4}_/, '');
  
  // Converti camelCase e PascalCase aggiungendo spazi prima delle maiuscole
  cleanName = cleanName.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Converti underscore in spazi
  cleanName = cleanName.replace(/_/g, ' ');
  
  // Capitalizza la prima lettera di ogni parola
  cleanName = cleanName.replace(/\b\w/g, l => l.toUpperCase());
  
  // Pulisci spazi multipli
  cleanName = cleanName.replace(/\s+/g, ' ').trim();
  
  return cleanName;
};

  // MODIFICATO: Aggiungiamo un gestore unificato per tutti i cambi di campo
const handleUnifiedFieldChange = useCallback((currentData, field, value, setterFunction, allowAutoDescription = true) => {
  console.log(`[CampiTabellaModal] handleUnifiedFieldChange: ${field} = ${value}`);
  
  const updatedData = { ...currentData };
  updatedData[field] = value;

  // ✅ AUTO-DESCRIPTION: Existing logic
  if (field === 'CAM_Nome' && allowAutoDescription) {
    if (value && value.trim()) {
      const autoDescription = formatNameToDescription(value.trim());
      console.log(`[CampiTabellaModal] Auto-description generated: "${autoDescription}"`);
      updatedData.CAM_Descrizione = autoDescription;
    } else {
      updatedData.CAM_Descrizione = '';
    }
  }

  // ✅ Auto-set/reset CAM_Lunghezza per tipi con lunghezza fissa (Data e ora)
  if (field === 'CAM_Id_TipiCampi') {
    const previousLength = currentData.CAM_Lunghezza;
    
    // Tipi con lunghezza fissa a 26 (basato su TIP_LunghezzaFissa dalla tabella SKE_TipiCampi)
    const tipiConLunghezzaFissa26 = [3, 16]; // "Data e ora" e "Data e ora (null)"
    // Tipi numerici (Intero, Decimale, Long, Decimale BIGDECIMAL)
    const tipiNumerici = [2, 5, 13, 15];
    
    if (tipiConLunghezzaFissa26.includes(value)) {
      // Se selezioniamo un tipo con lunghezza fissa, impostiamo 26
      console.log(`[CampiTabellaModal] Auto-setting CAM_Lunghezza to 26 for field type ID ${value}`);
      updatedData.CAM_Lunghezza = 26;
    } else if (tipiNumerici.includes(value) && updatedData.CAM_Extra_Tabelle) {
      // Numerico + FK tabella esterna → lunghezza fissa 11
      console.log(`[CampiTabellaModal] Auto-setting CAM_Lunghezza to 11 for numeric type with FK (type ID ${value})`);
      updatedData.CAM_Lunghezza = 11;
    } else if (previousLength === 26) {
      // Se deselezioniamo un tipo con lunghezza fissa (la lunghezza era 26), resettiamo a vuoto
      console.log(`[CampiTabellaModal] Resetting CAM_Lunghezza from 26 to empty for field type ID ${value}`);
      updatedData.CAM_Lunghezza = '';
    }
    // Altrimenti manteniamo il valore attuale di CAM_Lunghezza
  }

  // ✅ Auto-set CAM_Lunghezza quando si seleziona CAM_Extra_Tabelle su un campo numerico
  if (field === 'CAM_Extra_Tabelle') {
    const tipiNumerici = [2, 5, 13, 15];
    const tipoCorrente = Number(updatedData.CAM_Id_TipiCampi);
    if (tipiNumerici.includes(tipoCorrente) && value) {
      console.log(`[CampiTabellaModal] Auto-setting CAM_Lunghezza to 11 for numeric type with FK (Extra_Tabelle=${value})`);
      updatedData.CAM_Lunghezza = 11;
    } else if (tipiNumerici.includes(tipoCorrente) && !value) {
      // Se si rimuove la tabella esterna e il tipo è numerico, svuota la lunghezza
      console.log(`[CampiTabellaModal] Resetting CAM_Lunghezza for numeric type after FK removal`);
      updatedData.CAM_Lunghezza = '';
    }
  }

  setterFunction(updatedData);
}, []);

  // NUOVO: Gestore specifico per i campi in editing (SENZA auto-descrizione per campi esistenti)
  const handleEditingFieldChange = useCallback((field, value) => {
    const isNewDuplicate = editingCampoData._isNewDuplicate || editingCampoData.CAM_Id_Campi < 0;
    // Solo per i campi duplicati (non salvati ancora) abilita l'auto-descrizione
    handleUnifiedFieldChange(editingCampoData, field, value, setEditingCampoData, isNewDuplicate);
  }, [editingCampoData, handleUnifiedFieldChange]);

  // NUOVO: Gestore specifico per i nuovi campi (CON auto-descrizione)
  const handleNewFieldChange = useCallback((field, value) => {
    handleUnifiedFieldChange(newCampoData, field, value, setNewCampoData, true);
  }, [newCampoData, handleUnifiedFieldChange]);

  // MODIFICATO: Aggiorna renderFormFields per accettare un gestore custom
  const renderFormFields = (data, onChange, isExpanded = false, onToggleExpand) => {
    const fieldsToRender = isExpanded ? [...mainFields, ...extendedFields] : mainFields;

    const fieldLabels = {
      CAM_Nome: 'Nome Campo',
      CAM_Descrizione: 'Descrizione',
      CAM_Id_TipiCampi: 'Tipo Campo',
      CAM_Sequenza: 'Sequenza',
      CAM_Lunghezza: 'Lunghezza',
      CAM_Nullo: 'Nullo',
      CAM_Incrementale: 'Incrementale',
      CAM_Like: 'Ricerca Like',
      CAM_AccettaZero: 'Accetta Zero',
      CAM_Scollegato: 'Scollegato',
      CAM_ListaInFiltro: 'Tipo di ricerca in filtro',
      CAM_Default: 'Default',
      CAM_Extra_Tabelle: 'Tabella Esterna (FK)',
      CAM_NomeCampoChiaveEsterno: 'Nome Campo Chiave Esterna',
      CAM_Suffisso: 'Suffisso',
      // HUB/MILO
      CAM_ChiaveNaturale: 'Chiave Naturale',
      CAM_Cercabile: 'Cercabile',
      CAM_Versioned: 'Versioned (SCD2)',
      CAM_Cifrato: 'Cifrato',
      CAM_NomeUi: 'Nome UI',
      CAM_ErroreValidazione: 'Messaggio Errore Validazione',
    };

    const fieldTypes = {
      CAM_Descrizione: 'textarea',
      CAM_Nullo: 'boolean',
      CAM_Incrementale: 'boolean',
      CAM_Like: 'boolean',
      CAM_AccettaZero: 'boolean',
      CAM_Scollegato: 'boolean',
      CAM_ListaInFiltro: 'select',
      CAM_Id_TipiCampi: 'select',
      CAM_Extra_Tabelle: 'select',
      // HUB/MILO
      CAM_ChiaveNaturale: 'boolean',
      CAM_Cercabile: 'boolean',
      CAM_Versioned: 'boolean',
      CAM_Cifrato: 'boolean',
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
          <h4 className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-200`}>
            {isExpanded ? 'Configurazione Completa' : 'Configurazione Base'}
          </h4>
          <button
            onClick={onToggleExpand}
            className={`px-3 py-1 text-sm rounded-md transition-all duration-200 flex items-center space-x-1 ${
              isExpanded 
                ? `bg-${themeColor}-100 text-${themeColor}-700 hover:bg-${themeColor}-200 dark:bg-${themeColor}-900/30 dark:text-${themeColor}-300 dark:hover:bg-${themeColor}-800/30`
                : `bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700`
            }`}
          >
            {isExpanded ? <FaCompress className="w-3 h-3" /> : <FaExpand className="w-3 h-3" />}
            <span>{isExpanded ? 'Compatta' : 'Espandi'}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4">
          {fieldsToRender.map(field => {
            const fieldType = fieldTypes[field] || 'text';
            const isSelect = fieldType === 'select';
            let currentLookupOptions = [];
            if (isSelect) {
              if (field === 'CAM_Id_TipiCampi') {
                currentLookupOptions = lookupOptions.tipiCampiLookup || [];
              } else if (field === 'CAM_Extra_Tabelle') {
                currentLookupOptions = lookupOptions.tabelleLookup || [];
              } else if (field === 'CAM_ListaInFiltro') {
                currentLookupOptions = [
                  { value: 1, label: 'Lista' },
                  { value: 2, label: 'Singolo' },
                  { value: 3, label: 'Entrambe' },
                ];
              }
            }
            
            // AGGIUNTO: Placeholder informativo per la descrizione
            let placeholder = '';
            if (field === 'CAM_Descrizione') {
              const currentName = data.CAM_Nome || '';
              if (currentName.trim()) {
                placeholder = `Auto: ${formatNameToDescription(currentName)}`;
              } else {
                placeholder = 'Inserisci una descrizione...';
              }
            }

            // Determina se CAM_Lunghezza è required (campi numerici) o locked (numerico + FK)
            const isNumericoType = TIPI_NUMERICI.includes(Number(data.CAM_Id_TipiCampi));
            const isLengthRequired = field === 'CAM_Lunghezza' && isNumericoType;
            const isLengthLocked = field === 'CAM_Lunghezza' && isNumericoType && data.CAM_Extra_Tabelle;

            return (
              <FormInput
                key={field}
                field={field}
                label={fieldLabels[field] || field}
                value={data[field]}
                onChange={onChange} // IMPORTANTE: Usa il gestore passato come parametro
                inputType={fieldType}
                lookupOptions={currentLookupOptions}
                isLoadingLookups={isLoadingLookups}
                required={['CAM_Nome', 'CAM_Id_TipiCampi'].includes(field) || isLengthRequired}
                disabled={isLengthLocked}
                placeholder={placeholder}
                error={errors[field]}
              />
            );
          })}
        </div>
        
        {/* Messaggio informativo per l'auto-generazione della descrizione */}
        {(fieldsToRender.includes('CAM_Nome') || fieldsToRender.includes('CAM_Descrizione')) && (
          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-600 dark:text-blue-400">
            💡 <strong>Suggerimento:</strong> La descrizione si aggiorna automaticamente dal nome del campo 
            {/* Mostra il messaggio solo per campi nuovi o duplicati */}
            {((editingCampoData._isNewDuplicate || editingCampoData.CAM_Id_Campi < 0) || isAdding) && 
              '. Modifica manualmente la descrizione per disabilitare l\'auto-aggiornamento.'
            }
            {/* Per campi esistenti, mostra un messaggio diverso */}
            {!(editingCampoData._isNewDuplicate || editingCampoData.CAM_Id_Campi < 0) && !isAdding && 
              ' (solo per nuovi campi e duplicati).'
            }
          </div>
        )}
        
        <div className="flex justify-end mt-2">
          {isExpanded && (
            <button
              onClick={onToggleExpand}
              className={`px-3 py-1 text-sm rounded-md transition-all duration-200 flex items-center space-x-1 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700`}
            >
              <FaCompress className="w-3 h-3" />
              <span>Compatta</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleCancelEdit = () => {
    const isNewDuplicate = editingCampoData._isNewDuplicate || editingCampoData.CAM_Id_Campi < 0;
    
    if (isNewDuplicate) {
      // Se era un campo duplicato non ancora salvato, rimuovilo dalla vista locale
      const tempId = editingCampoData.CAM_Id_Campi;
      const originalSequenza = Number(editingCampoData.CAM_Sequenza) || 0;
      
      console.log('❌ CampiTabellaModal: Annullando duplicato locale con ID:', tempId);
      
      setDisplayCampi(prevCampi => {
        // Rimuovi il campo duplicato temporaneo
        const campiSenzaDuplicato = prevCampi.filter(c => c.CAM_Id_Campi !== tempId);
        
        // Ripristina le sequenze originali dei campi che erano stati spostati
        const campiRipristinati = campiSenzaDuplicato.map(campo => {
          const seq = Number(campo.CAM_Sequenza) || 0;
          if (seq > originalSequenza && seq <= 999) {
            return {
              ...campo,
              CAM_Sequenza: seq - 1
            };
          }
          return campo;
        });
        
        return campiRipristinati.sort((a, b) => (Number(a.CAM_Sequenza) || 0) - (Number(b.CAM_Sequenza) || 0));
      });
      
      showSuccess('Duplicazione annullata');
    }
    
    setEditingCampoId(null);
    setEditingCampoData({});
    setIsAdding(false);
    setErrors({});
  };

  const handleDuplicateCancel = () => {
    setIsDuplicateModalOpen(false);
  };

  // AGGIUNTO: Gestore per salvare la configurazione di ordinamento
  const handleOrdinamentoSave = async (campiDaAggiornare) => {
    if (!campiDaAggiornare || campiDaAggiornare.length === 0) {
      showError('Nessun campo da aggiornare');
      return;
    }

    console.log('🔢 CampiTabellaModal: Salvando configurazione ordinamento', campiDaAggiornare.length, 'campi');
    
    isPerformingOperation.current = true;
    setIsSaving(true);
    
    try {
      // Prepara le operazioni di aggiornamento
      const changes = campiDaAggiornare.map(campo => ({
        operation: 'update',
        data: {
          CAM_Id_Campi: campo.CAM_Id_Campi,
          CAM_ProgressivoOrdinamento: campo.CAM_ProgressivoOrdinamento,
          CAM_TipoOrdinamento: campo.CAM_TipoOrdinamento
        }
      }));

      const response = await apiService.post('/crud/SKE_Campi', { changes });
      
      if (response && response.results && response.results.success) {
        showSuccess('Configurazione ordinamento salvata con successo!');
        
        // Refresh immediato per mostrare le modifiche
        executeImmediateRefresh();
        
        // Chiudi la modale di ordinamento
        setIsOrdinamentoModalOpen(false);
      } else {
        showError('Errore durante il salvataggio della configurazione');
        isPerformingOperation.current = false;
      }
    } catch (error) {
      console.error('Errore durante il salvataggio ordinamento:', error);
      showError(`Errore: ${error.message}`);
      isPerformingOperation.current = false;
    } finally {
      setIsSaving(false);
    }
  };

  // AGGIUNTO: Evita il render se la modal non è aperta
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className={`flex justify-between items-center p-4 border-b bg-${themeColor}-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700`}>
            <h2 className={`text-xl font-bold text-${themeColor}-800 dark:text-gray-100`}>
              {/* CORREZIONE: Gestione più robusta per ID e nome macrofunzione */}
              Gestione Campi - {macrofunzioneData?.MAC_Nome ? 
                `${macrofunzioneData.MAC_Nome} (ID: ${macrofunzioneData.MAC_Id_MacroFunzioni || macrofunzioneData.id || 'N/A'})` : 
                `Tabella ID: ${currentTableId}`
              }
            </h2>
            <button onClick={onClose} className="mr-4"><FaTimes className="h-6 w-6 text-gray-600 dark:text-gray-300" /></button>
          </div>
          
          {/* Toolbar */}
          <div className="p-4 border-b flex justify-between items-center">
            <span className="text-sm dark:text-gray-100">Campi: {displayCampi.length}</span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  const wasAdding = isAdding;
                  // NUOVO: quando si entra in modalità "Nuovo Campo" applica i default modulo-specifici
                  if (!wasAdding) {
                    const maxSeq = displayCampi.reduce((m, c) => {
                      const s = Number(c.CAM_Sequenza) || 0;
                      return s > 0 && s <= 1000 && s > m ? s : m;
                    }, 0);
                    setNewCampoData(prev => ({
                      ...prev,
                      CAM_Versioned: true, // Coerente con CAM_Versioned DEFAULT 1 dello schema Milo
                      ...(isHubTable && { CAM_Sequenza: maxSeq + 10 }),
                    }));
                  }
                  setIsAdding(!isAdding);
                  if (!wasAdding) {
                    setTimeout(() => {
                      addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                  }
                }}
                className={`px-4 py-2 text-sm rounded-lg text-white ${isAdding ? 'bg-red-500' : `bg-${themeColor}-600`}`}
              >
                {isAdding ? 'Annulla' : 'Nuovo Campo'}
              </button>
              <button
                onClick={() => setIsDuplicateModalOpen(true)}
                className={`px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center space-x-2`}
              >
                <FaClone className="w-4 h-4" />
                <span>Duplica da Tabella</span>
              </button>
              {/* AGGIUNTO: Tasto Censisci automatico item */}
              <button
                onClick={handleCensimentoAutomaticoItem}
                disabled={!currentTableId || isCensimentoInCorso}
                className={`px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                title="Genera automaticamente gli Item per questa tabella"
              >
                {isCensimentoInCorso ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    <span>Censimento...</span>
                  </>
                ) : (
                  <>
                    <FaCogs className="w-4 h-4" />
                    <span>Censisci automatico Item</span>
                  </>
                )}
              </button>
              {/* AGGIUNTO: Pulsante per visualizzare i risultati */}
              {generationResult && (
                <button
                  onClick={handleShowResults}
                  className={`px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center space-x-2`}
                >
                  <FaEye className="w-4 h-4" />
                  <span>Visualizza Risultati</span>
                </button>
              )}
              {/* AGGIUNTO: Pulsante per navigare alla pagina delle tabelle */}
              <button
                onClick={handleNavigateToTabelle}
                disabled={!currentTableId}
                className={`px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                title="Vai alla pagina delle tabelle con filtro per questa tabella"
              >
                <FaExternalLinkAlt className="w-4 h-4" />
                <span>Vai a Tabelle</span>
              </button>
              {displayCampi.length > 1 && (
                <button onClick={() => setIsSequenceManagerOpen(true)} className="px-4 py-2 text-sm rounded-lg bg-gray-500 text-white">
                  Riordina
                </button>
              )}
              <button 
                onClick={() => setIsOrdinamentoModalOpen(true)}
                disabled={!currentTableId}
                className={`px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
                title="Configura l'ordinamento dei campi (CAM_ProgressivoOrdinamento e CAM_TipoOrdinamento)"
              >
                <FaSortAmountDown className="w-4 h-4" />
                <span>Orderby default</span>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-grow overflow-auto p-4">
            {isLoadingCampi && <div className="text-center"><FaSpinner className="animate-spin text-2xl" /></div>}
            
            {/* Form di aggiunta */}
            {isAdding && (
              <div ref={addFormRef} className="p-4 mb-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                <h3 className="font-bold mb-2 text-gray-900 dark:text-gray-100">Aggiungi Nuovo Campo</h3>
                {renderFormFields(
                  newCampoData, 
                  handleNewFieldChange, // MODIFICATO: Usa il gestore specifico per nuovi campi
                  isFormExpanded,
                  () => setIsFormExpanded(!isFormExpanded)
                )}
                <button onClick={handleAddNewCampo} className={`mt-2 px-4 py-2 text-sm rounded-lg text-white bg-${themeColor}-600`}>
                  Salva
                </button>
              </div>
            )}

            {/* Tabella dei campi */}
            {!isLoadingCampi && displayCampi.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {isTableExpanded && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('CAM_Sequenza')}>
                        <div className="flex items-center">
                          Sequenza
                          {renderSortIcon('CAM_Sequenza')}
                        </div>
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('CAM_Nome')}>
                      <div className="flex items-center">
                        Nome Campo
                        {renderSortIcon('CAM_Nome')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('CAM_Descrizione')}>
                      <div className="flex items-center">
                        Descrizione
                        {renderSortIcon('CAM_Descrizione')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('CAM_Id_TipiCampi')}>
                      <div className="flex items-center">
                        Tipo
                        {renderSortIcon('CAM_Id_TipiCampi')}
                      </div>
                    </th>
                    {isTableExpanded && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('CAM_Extra_Tabelle')}>
                          <div className="flex items-center">
                            FK Tabella
                            {renderSortIcon('CAM_Extra_Tabelle')}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Lunghezza
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Flags
                        </th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {displayCampi.map((campo) => (
                    <tr key={campo.CAM_Id_Campi} ref={editingCampoId === campo.CAM_Id_Campi ? editRowRef : null} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${editingCampoId === campo.CAM_Id_Campi ? `bg-${themeColor}-50 dark:bg-${themeColor}-900/20` : ''}`}>
                      {editingCampoId === campo.CAM_Id_Campi ? (
                        <td colSpan={isTableExpanded ? 8 : 5} className="p-0">
                          <div className={`p-4 bg-white dark:bg-gray-800 border-l-4 border-${themeColor}-500`}>
                            <div className="flex justify-between items-center mb-3">
                              <h4 className={`text-base font-semibold text-${themeColor}-800 dark:text-${themeColor}-200`}>
                                Modifica Campo: <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{editingCampoData.CAM_Nome}</span>
                              </h4>
                            </div>
                            
                            {renderFormFields(
                              editingCampoData,
                              handleEditingFieldChange, // MODIFICATO: Usa il gestore specifico per campi in editing
                              isEditFormExpanded,
                              () => setIsEditFormExpanded(!isEditFormExpanded)
                            )}

                            <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                              <button onClick={handleCancelEdit} disabled={isSaving} className="px-5 py-2 text-sm font-medium border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 flex items-center space-x-2">
                                <FaTimes className="w-4 h-4" />
                                <span>Annulla</span>
                              </button>
                              <button onClick={handleSaveEdit} disabled={isSaving} className={`px-5 py-2 text-sm font-medium bg-${themeColor}-600 text-white rounded-lg hover:bg-${themeColor}-700 transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 shadow-md`}>
                                {isSaving ? (
                                  <>
                                    <FaSpinner className="animate-spin w-4 h-4" />
                                    <span>Salvando...</span>
                                  </>
                                ) : (
                                  <>
                                    <FaSave className="w-4 h-4" />
                                    <span>Salva Modifiche</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                      ) : (
                        <>
                          {isTableExpanded && (
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                              <div className="flex items-center space-x-2">
                                <span className="font-mono text-gray-900 dark:text-gray-100">{campo.CAM_Sequenza}</span>
                                {isReadOnlyCampo(campo) && <FaLock className="text-red-500 w-3 h-3" title="Campo protetto" />}
                              </div>
                            </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono">{campo.CAM_Nome}</span>
                              {!isTableExpanded && isReadOnlyCampo(campo) && <FaLock className="text-red-500 w-3 h-3" title="Campo protetto" />}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 max-w-xs truncate" title={campo.CAM_Descrizione}>
                            {campo.CAM_Descrizione || <span className="italic text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              {campo.CAM_Id_TipiCampi_text || campo.CAM_Id_TipiCampi || '-'}
                            </span>
                          </td>
                          {isTableExpanded && (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                                {campo.CAM_Extra_Tabelle_text || (campo.CAM_Extra_Tabelle ? campo.CAM_Extra_Tabelle : <span className="italic text-gray-400">-</span>)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                                {campo.CAM_Lunghezza || <span className="italic text-gray-400">-</span>}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                                <div className="flex flex-wrap gap-1">
                                  {campo.CAM_Nullo && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-md">
                                      NULL
                                    </span>
                                  )}
                                  {campo.CAM_Incrementale && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-md">
                                      AUTO
                                    </span>
                                  )}
                                  {campo.CAM_AccettaZero && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-md">
                                      Accetta zero
                                    </span>
                                  )}
                                  {campo.CAM_Like && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 rounded-md">
                                      Ricerca Like
                                    </span>
                                  )}
                                  {campo.CAM_Scollegato && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-md">
                                      Scollegato  
                                    </span>
                                  )}
                                  {campo.CAM_ListaInFiltro && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-md">
                                      FILTRO
                                    </span>
                                  )}
                                </div>
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                            <div className="flex items-center space-x-2">
                              {/* NUOVO (solo HUB): pulsante per gestire SKE_CampiOperatori */}
                              {isHubTable && campo.CAM_Id_Campi > 0 && (
                                <button
                                  onClick={() => setOperatoriEditorState({
                                    isOpen: true,
                                    campoId: campo.CAM_Id_Campi,
                                    campoNome: campo.CAM_Nome || '',
                                    cercabile: !!campo.CAM_Cercabile,
                                  })}
                                  className={`p-2 rounded-lg transition-all duration-200 ${
                                    campo.CAM_Cercabile
                                      ? 'text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/30'
                                      : 'text-yellow-600 hover:bg-yellow-100 dark:text-yellow-400 dark:hover:bg-yellow-900/30'
                                  }`}
                                  title={campo.CAM_Cercabile
                                    ? 'Gestisci operatori di ricerca'
                                    : 'Campo non cercabile: gli operatori esistenti non saranno emessi nei template'}
                                >
                                  <FaCogs className="w-4 h-4" />
                                </button>
                              )}
                              {/* MODIFICATO: Pulsante Duplica - disabilitato per campi protetti */}
                              <button
                                onClick={() => handleDuplicateCampo(campo)}
                                disabled={isSaving || isReadOnlyCampo(campo)}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  isReadOnlyCampo(campo)
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30'
                                }`}
                                title={isReadOnlyCampo(campo) ? "Campo protetto - non duplicabile" : "Duplica campo"}
                              >
                                <FaCopy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditCampo(campo)}
                                disabled={isSaving || isReadOnlyCampo(campo)}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  isReadOnlyCampo(campo)
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : `text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30`
                                }`}
                                title={isReadOnlyCampo(campo) ? "Campo protetto" : "Modifica campo"}
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCampo(campo.CAM_Id_Campi)}
                                disabled={isSaving || isReadOnlyCampo(campo)}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  isReadOnlyCampo(campo)
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : `text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30`
                                }`}
                                title={isReadOnlyCampo(campo) ? "Campo protetto" : "Elimina campo"}
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Empty state */}
            {!isLoadingCampi && displayCampi.length === 0 && (
              <div className="text-center py-12">
                <FaDatabase className="mx-auto mb-4 text-4xl text-gray-400" />
                <h3 className="text-lg font-medium text-gray-600">Nessun campo configurato</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Aggiungi un nuovo campo o duplica da una tabella esistente.
                </p>
                <button
                  onClick={() => setIsDuplicateModalOpen(true)}
                  className={`px-4 py-2 bg-${themeColor}-600 text-white rounded-lg hover:bg-${themeColor}-700 flex items-center mx-auto`}
                >
                  <FaClone className="mr-2" />
                  Duplica Campi da Tabella
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal per la duplicazione da tabella */}
      <DuplicaCampiModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onDuplicate={handleDuplicateSubmit}
        tabelleOptions={lookupOptions?.tabelleLookup || []}
        themeColor={themeColor}
      />

      {/* Modal per il riordinamento */}
      {isSequenceManagerOpen && (
        <DragDropSequenceManager
          isOpen={isSequenceManagerOpen}
          onClose={() => setIsSequenceManagerOpen(false)}
          items={displayCampi}
          onSave={handleSequenceSave}
          displayField="CAM_Nome"
          descriptionField="CAM_Descrizione"
          idField="CAM_Id_Campi"
          sequenceField="CAM_Sequenza"
          title="Riordina Campi"
          themeColor={themeColor}
          // COMMENTO: Configurazione del range di sequenze modificabili (1-999 per escludere campi di sistema)
          minSequence={1}
          maxSequence={999}
          // COMMENTO: Passo di incremento per la sequenza (opzionale, ma utile per compatibilità)
          sequenceStep={1}
        />
      )}

      {/* AGGIUNTO: Modal per configurare ordinamento (CAM_ProgressivoOrdinamento e CAM_TipoOrdinamento) */}
      {isOrdinamentoModalOpen && (
        <OrdinamentoCampiModal
          isOpen={isOrdinamentoModalOpen}
          onClose={() => setIsOrdinamentoModalOpen(false)}
          campi={displayCampi}
          onSave={handleOrdinamentoSave}
          themeColor={themeColor}
        />
      )}

      {/* NUOVO: Sub-editor SKE_CampiOperatori (visibile per modulo HUB) */}
      <CampiOperatoriEditor
        isOpen={operatoriEditorState.isOpen}
        onClose={() => setOperatoriEditorState(s => ({ ...s, isOpen: false }))}
        campoId={operatoriEditorState.campoId}
        campoNome={operatoriEditorState.campoNome}
        cercabile={operatoriEditorState.cercabile}
        themeColor={themeColor}
      />

      {/* Modale differenze censimento */}
      <CensimentoDifferencesModal
        isOpen={showDiffModal}
        differences={pendingDifferences}
        tableName={pendingTableName}
        onConfirm={handleDiffConfirm}
        onCancel={handleDiffCancel}
        themeColor={themeColor}
      />

      {/* AGGIUNTO: Modal per i risultati (alla fine del componente, dopo gli altri modals) */}
      {showResultModal && generationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className={`flex justify-between items-center px-6 py-4 bg-${themeColor}-100 dark:bg-${themeColor}-900 border-b border-gray-300 dark:border-gray-700`}>
              <h3 className={`text-lg font-semibold text-${themeColor}-800 dark:text-${themeColor}-100`}>
                Risultati Censimento Automatico Item
              </h3>
              <button 
                onClick={() => setShowResultModal(false)}
                className={`text-${themeColor}-600 hover:text-${themeColor}-800 dark:text-${themeColor}-400 dark:hover:text-${themeColor}-200`}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                <strong>Timestamp:</strong> {new Date(generationResult.timestamp).toLocaleString()}
              </div>
              {renderResultDetails()}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Dati Raw (Debug)</h4>
                <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-xs overflow-x-auto border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200">
                  {JSON.stringify(generationResult.data, null, 2)}
                </pre>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button 
                onClick={() => setShowResultModal(false)}
                className={`px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CampiTabellaModal;
